import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { API_BASE_URL } from "@/lib/constants";
import { trackEvent } from "@/lib/analytics";

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/newsletter`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setIsSubscribed(true);
        trackEvent('newsletter_signup');
      } else {
        throw new Error("Failed to subscribe");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-r from-soft-blush to-dusty-rose">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-charcoal dark:text-white mb-4">
          Never Miss a Find
        </h2>
        <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
          Get my monthly edit delivered to your inbox. New discoveries, styling tips, and exciting new tools to make shopping and getting dressed that little bit easier.
        </p>

        {isSubscribed ? (
          <div className="max-w-md mx-auto">
            <p className="text-2xl font-serif font-bold text-charcoal dark:text-white mb-2">
              Welcome to the family!
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Thank you for subscribing. I can't wait to share my latest finds with you.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <Input
                id="newsletter-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="flex-1 h-11 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <Button
                type="submit"
                disabled={isLoading}
                size="lg"
              >
                {isLoading ? "Subscribing..." : "Subscribe"}
              </Button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-4">
              By subscribing, you agree to our{" "}
              <a href="/privacy" className="underline hover:no-underline">
                Privacy Policy
              </a>{" "}
              and{" "}
              <a href="/terms-and-conditions" className="underline hover:no-underline">
                Terms &amp; Conditions
              </a>
              .
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
